import { Button } from "@loot-os/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@loot-os/ui/components/command";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

/**
 * A searchable country picker over whichever list the page hands it: Stripe's connected-account
 * countries on the Payments page, every ISO code on the shipping pages. `disabledCodes` greys out
 * countries another row already holds.
 */
export function CountryCombobox({
  id,
  value,
  onChange,
  countries,
  disabledCodes,
  disabled,
  placeholder = "Select country…",
  size = "default",
}: {
  id?: string;
  value: string;
  onChange: (code: string) => void;
  countries: readonly { code: string; name: string }[];
  disabledCodes?: ReadonlySet<string>;
  disabled?: boolean;
  placeholder?: string;
  /** `sm` matches the compact inputs of a table row. */
  size?: "default" | "sm";
}) {
  const [open, setOpen] = useState(false);
  const selected = countries.find((c) => c.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            size={size}
            className="w-full justify-between font-normal"
          />
        }
      >
        <span className="truncate">
          {selected ? `${selected.name} (${selected.code})` : placeholder}
        </span>
        <ChevronsUpDown className="opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-(--anchor-width) p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country…" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {countries.map((c) => (
                <CommandItem
                  key={c.code}
                  value={c.name}
                  keywords={[c.code]}
                  disabled={c.code !== value && disabledCodes?.has(c.code)}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                  }}
                >
                  <Check className={c.code === value ? "opacity-100" : "opacity-0"} />
                  {c.name}
                  <span className="ml-auto font-mono text-xs text-muted-foreground">{c.code}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
